import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogActions } from '@angular/material/dialog';
import { b64toBlob } from '@app/api/core/utils/file.util';
import { AbstractReactiveDialogComponent } from '@app/shared/components/dialog/reactive-dialog/base/abstract-reactive-dialog-component';
import { ReactiveDialogContentComponent } from '@app/shared/components/dialog/reactive-dialog/base/reactive-dialog-content/reactive-dialog-content.component';
import { BasicIconButtonComponent } from '@app/shared/components/form/basic-icon-button/basic-icon-button.component';
import { TypedFormGroupConfig } from '@app/shared/components/form/gewerbe/product-selection-row/product-selection.types';
import { UploadFileComponent } from '@app/shared/components/upload-file/upload-file.component';
import { SimpleDocument } from '@app/shared/components/upload-file/upload-file.types';
import { DataService } from '@app/shared/services/data/data.service';
import { LowLevelApi } from '@app/shared/services/state/low-level-api.service';
import { MessageService } from '@app/shared/services/ui/message/message.service';
import { AntragDocumentUploadDialogContext } from '@app/views/vertriebscockpit/offerteantraege/makler/nicht-freigegebene-antraege/antrag-document-upload-dialog/antrag-document-upload-dialog.component';
import { MaklerPolizzeDetails, PolizzeResult, VollmachtOption } from '@o30/entities';
import FileSaver from 'file-saver';
import { filter, map, take } from 'rxjs/operators';

@Component({
  selector:    'app-vollmacht-dialog',
  imports:     [
    BasicIconButtonComponent,
    MatDialogActions,
    ReactiveDialogContentComponent,
    UploadFileComponent,
    ReactiveFormsModule
  ],
  templateUrl: './vollmacht-dialog.component.html',
  styleUrl:    './vollmacht-dialog.component.scss',
  standalone:  true
})
export class VollmachtDialogComponent extends AbstractReactiveDialogComponent<PolizzeResult, MaklerPolizzeDetails, AntragDocumentUploadDialogContext, never> {

  protected readonly VollmachtOption = VollmachtOption;

  private dataService = inject(DataService);
  private messageService = inject(MessageService);
  private llapi = inject(LowLevelApi);
  private destroyRef = inject(DestroyRef);

  protected currentDocuments: SimpleDocument[] = [];
  protected uploadFileCallback = this.dataService.uploadFileMaklerDocument.bind(this.dataService);
  protected vollmachtForm: FormGroup<TypedFormGroupConfig<{
    vollmachtOption: VollmachtOption | null;
  }>>;

  constructor(private fb: FormBuilder) {
    super();
    this.dialogInputData.title = 'Vollmacht';
    this.vollmachtForm = this.fb.group({
      vollmachtOption: [null]
    });
  }

  submit() {
    this.dataService.putPolizzeMaklerVollmacht(this.polizzeResult.id, {
        vollmachtOption: this.vollmachtForm.value.vollmachtOption,
        polizzeId:       this.polizzeResult.id,
        fileUpload:      {
          fileId:   this.firstDocument?.key,
          fileName: this.firstDocument?.name,
          user:     null,
          mimetype: null
        },
        documentType:    this.context.maklerDocumentType
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        filter(_payload => this.messageService.handlePayloadReturnSuccess(_payload))
      )
      .subscribe(
        _payload => this.dialogRef.close({
          status: 'success',
          data:   _payload.data
        })
      );
  }

  createDocument() {
    // TODO error handling
    this.llapi.patchValue('layout.spinner', true);
    this.dataService.getPolizzePrintAntrag(this.polizzeResult.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        filter(_payload => this.messageService.handlePayloadReturnSuccess(_payload)),
        map(_payload => _payload.data)
      )
      .subscribe(
        _file => {
          const data = b64toBlob(_file.value, 'application/pdf');
          FileSaver.saveAs(data, _file.key);
          this.llapi.patchValue('layout.spinner', false);
        }
      );
  }

  get polizzeResult() {
    return this.data;
  }

  // only 1 document is allowed
  get firstDocument() {
    return this.currentDocuments[0];
  }

  get isSaveEnabled() {
    return this.vollmachtForm.get('vollmachtOption')?.value === VollmachtOption.LIEGT_AUF
     || this.currentDocuments?.length;
  }
}
